import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2, ExternalLink, KeyRound, Upload, Copy } from "lucide-react";
import { type AgenticIntegration, type AuthMethod } from "../../data/agenticIntegrations";
import { BrandIcon, type BrandSlug } from "../BrandIcon";
import {
  getConnection, disconnect,
  initiateOAuth, submitApiKey, submitBasicAuth, submitServiceAccount, fetchWebhook,
  type ConnectionRecord,
} from "../../lib/connectionStore";

/* Per-auth-method Connect dispatcher.
 *
 *   oauth2          → "Continue with <Provider>" → /api/connections/:app POST
 *                     returns initiate_url → popup → backend stores tokens
 *   api_key         → secret input → POST { key }
 *   basic_auth      → user + pass → POST { user, password }
 *   service_account → JSON file upload → POST { json }
 *   webhook         → server returns { url, secret } to copy
 *
 * No simulateConnect. Every branch hits a real Pages Function that
 * proxies to n8n (or the agent's credential_proxy once deployed).
 */

interface Props {
  app: AgenticIntegration | null;
  onClose: () => void;
  onChanged?: () => void;
}

const AUTH_COPY: Record<AuthMethod, { primaryLabel: (provider: string) => string; hint: string }> = {
  oauth2:          { primaryLabel: (p) => `Continue with ${p}`,             hint: "You'll be redirected to the provider, then back here. Tokens are encrypted in the studio's tenant." },
  api_key:         { primaryLabel: () => "Save API key",                    hint: "Paste the API key from the provider's dashboard. We encrypt it at rest with AES-256-GCM." },
  basic_auth:      { primaryLabel: () => "Save credentials",                hint: "Used only for legacy hosts that don't support OAuth. Stored encrypted, never logged." },
  service_account: { primaryLabel: () => "Upload service-account JSON",     hint: "Drop the JSON file your admin downloaded from the provider's console. Never shared." },
  webhook:         { primaryLabel: () => "Reveal webhook URL & secret",     hint: "Paste these into the provider's webhook settings; we verify every request's signature." },
};

export function ConnectAppSheet({ app, onClose, onChanged }: Props): JSX.Element | null {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When the credential-proxy is known-unbound (404/503 from a prior
  // attempt), disable every "Continue / Save" action so the user
  // can't keep mashing the button to see the same error.
  // CLAUDE.md §5 — honest about what's not yet bound.
  const [backendUnbound, setBackendUnbound] = useState<boolean>(false);
  function noteBackendStatus(err: unknown): void {
    const msg = err instanceof Error ? err.message : String(err);
    if (/HTTP 404|HTTP 503|not yet bound|backend not yet|tunnel|unreachable/i.test(msg)) {
      setBackendUnbound(true);
    }
  }

  // Per-method state
  const [apiKey, setApiKey] = useState("");
  const [basicUser, setBasicUser] = useState("");
  const [basicPass, setBasicPass] = useState("");
  const [saFile, setSaFile] = useState<File | null>(null);
  const [webhook, setWebhook] = useState<{ url: string; secret: string } | null>(null);

  // Polling state for the OAuth popup flow.
  const [pollingFor, setPollingFor] = useState<string | null>(null);

  useEffect(() => {
    if (!pollingFor) return;
    const id = window.setInterval(() => {
      const c = getConnection(pollingFor);
      if (c.status === "connected") {
        setPollingFor(null);
        onChanged?.();
        onClose();
      }
    }, 1500);
    return () => window.clearInterval(id);
  }, [pollingFor, onChanged, onClose]);

  if (!app) return null;
  const current: ConnectionRecord = getConnection(app.name);
  const copy = AUTH_COPY[app.auth];

  async function runOAuth(): Promise<void> {
    if (!app) return;
    setError(null);
    setBusy(true);
    try {
      const { initiate_url } = await initiateOAuth(app.name);
      // Pop the OAuth window themed by the provider; on success the
      // provider redirects back to /api/connect/callback which closes
      // the popup and flips our connection state.
      window.open(initiate_url, "foyrmind-oauth", "width=520,height=720,noopener,noreferrer");
      setPollingFor(app.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : "OAuth init failed.");
      noteBackendStatus(e);
    } finally {
      setBusy(false);
    }
  }

  async function runApiKey(): Promise<void> {
    if (!app) return;
    setError(null);
    if (apiKey.trim().length < 6) {
      setError("API key looks too short — paste the full token from the provider.");
      return;
    }
    setBusy(true);
    try {
      await submitApiKey(app.name, apiKey.trim());
      onChanged?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save the API key.");
      noteBackendStatus(e);
    } finally {
      setBusy(false);
    }
  }

  async function runBasic(): Promise<void> {
    if (!app) return;
    setError(null);
    if (!basicUser.trim() || !basicPass) {
      setError("Both username and password are required.");
      return;
    }
    setBusy(true);
    try {
      await submitBasicAuth(app.name, basicUser.trim(), basicPass);
      onChanged?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save credentials.");
      noteBackendStatus(e);
    } finally {
      setBusy(false);
    }
  }

  async function runServiceAccount(): Promise<void> {
    if (!app || !saFile) return;
    setError(null);
    setBusy(true);
    try {
      const text = await saFile.text();
      let parsed: unknown;
      try { parsed = JSON.parse(text); } catch { throw new Error("That's not valid JSON."); }
      await submitServiceAccount(app.name, parsed);
      onChanged?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to upload service-account JSON.");
      noteBackendStatus(e);
    } finally {
      setBusy(false);
    }
  }

  async function loadWebhook(): Promise<void> {
    if (!app) return;
    setError(null);
    setBusy(true);
    try {
      const hook = await fetchWebhook(app.name);
      setWebhook(hook);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't generate webhook URL.");
      noteBackendStatus(e);
    } finally {
      setBusy(false);
    }
  }

  function unwire(): void {
    if (!app) return;
    disconnect(app.name);
    onChanged?.();
    onClose();
  }

  function primary(): void {
    switch (app!.auth) {
      case "oauth2":          return void runOAuth();
      case "api_key":         return void runApiKey();
      case "basic_auth":      return void runBasic();
      case "service_account": return void runServiceAccount();
      case "webhook":         return void loadWebhook();
    }
  }

  const primaryDisabled =
    busy ||
    backendUnbound ||
    (app.auth === "api_key" && apiKey.trim().length < 6) ||
    (app.auth === "basic_auth" && (!basicUser.trim() || !basicPass)) ||
    (app.auth === "service_account" && !saFile);

  return createPortal((
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-ink-950/50 backdrop-blur-sm grid place-items-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-2xl bg-cream w-[480px] max-w-full shadow-deep p-5 relative"
          onClick={(ev) => ev.stopPropagation()}
          role="dialog" aria-modal="true"
        >
          <button onClick={onClose} className="absolute top-3 right-3 text-ink-500 hover:text-ink-950" aria-label="Close">
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="grid place-items-center w-12 h-12 rounded-2xl border border-border/70 bg-cream shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
              <BrandIcon slug={app.brand as BrandSlug} group={app.group} size={26} title={app.name} />
            </div>
            <div>
              <div className="eyebrow">{app.group}</div>
              <div className="display-3 text-ink-950">{app.name}</div>
              <div className="text-[10.5px] text-ink-500 font-mono mt-0.5">auth: {app.auth}</div>
            </div>
          </div>

          <p className="text-[12.5px] text-ink-700 leading-snug mb-4">{app.agenticAction}</p>

          {current.status === "connected" ? (
            <div className="rounded-lg border border-fern/30 bg-fern/10 p-3 mb-3 flex items-start gap-2">
              <Check className="w-4 h-4 text-fern mt-0.5" />
              <div className="text-[12.5px]">
                <div className="text-ink-950 font-semibold">Connected</div>
                <div className="text-ink-700">{current.account ?? "—"}</div>
                {current.connectedAt ? (
                  <div className="text-[10.5px] text-ink-500 tabular mt-0.5">
                    since {new Date(current.connectedAt).toLocaleString()}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* ── OAuth2 branch ── */}
              {app.auth === "oauth2" ? (
                <div className="rounded-lg border border-border bg-cream-dark/30 p-3 text-[12px] text-ink-700">
                  <div className="font-semibold text-ink-950 mb-0.5">Sign in via {app.name}</div>
                  We'll open a secure consent window. After you approve, the cockpit shows a green chip — no token ever lives in your browser.
                  {pollingFor ? (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-foyr-deep">
                      <Loader2 className="w-3 h-3 animate-spin" /> Waiting for {app.name} consent…
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* ── API key branch ── */}
              {app.auth === "api_key" ? (
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-ink-500">API key</span>
                  <div className="mt-1 relative">
                    <KeyRound className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-500" />
                    <input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder={`Paste your ${app.name} API key`}
                      autoFocus
                      className="w-full bg-cream border border-border rounded-lg pl-8 pr-3 py-2 text-sm font-mono tracking-tight focus:outline-none focus:ring-2 focus:ring-foyr/30"
                    />
                  </div>
                </label>
              ) : null}

              {/* ── Basic auth branch ── */}
              {app.auth === "basic_auth" ? (
                <div className="grid grid-cols-1 gap-2">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-ink-500">Username</span>
                    <input
                      value={basicUser}
                      onChange={(e) => setBasicUser(e.target.value)}
                      placeholder="user@host"
                      className="mt-1 w-full bg-cream border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foyr/30"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-ink-500">Password</span>
                    <input
                      type="password"
                      value={basicPass}
                      onChange={(e) => setBasicPass(e.target.value)}
                      placeholder="••••••••"
                      className="mt-1 w-full bg-cream border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foyr/30"
                    />
                  </label>
                </div>
              ) : null}

              {/* ── Service-account branch ── */}
              {app.auth === "service_account" ? (
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-6 cursor-pointer hover:border-foyr/40 hover:bg-foyr/5 transition">
                  <Upload className="w-5 h-5 text-foyr-deep mb-1.5" />
                  <span className="text-[12.5px] text-ink-950 font-semibold">
                    {saFile ? saFile.name : "Drop the service-account JSON"}
                  </span>
                  <span className="text-[10.5px] text-ink-500 mt-0.5">Downloaded from the provider's console</span>
                  <input
                    type="file"
                    accept="application/json,.json"
                    className="hidden"
                    onChange={(e) => setSaFile(e.target.files?.[0] ?? null)}
                  />
                </label>
              ) : null}

              {/* ── Webhook branch ── */}
              {app.auth === "webhook" ? (
                webhook ? (
                  <div className="space-y-2">
                    <CopyField label="Webhook URL" value={webhook.url} />
                    <CopyField label="Signing secret" value={webhook.secret} secret />
                    <div className="text-[10.5px] text-ink-500">
                      Paste both into the provider's webhook settings. We verify every request's HMAC signature.
                    </div>
                  </div>
                ) : (
                  <div className="text-[12.5px] text-ink-700">
                    Click below — we'll mint a unique webhook URL + signing secret for this studio.
                  </div>
                )
              ) : null}

              {error ? (
                <div className={"rounded-lg border px-3 py-2 text-[11.5px] leading-snug " + (backendUnbound ? "border-foyr-deep/40 bg-foyr-deep/5 text-foyr-deep" : "border-foyr/40 bg-foyr/5 text-foyr-deep")}>
                  {error}
                  {backendUnbound ? (
                    <div className="mt-1.5 pt-1.5 border-t border-foyr-deep/20 text-[10.5px] text-ink-700 space-y-0.5">
                      <div className="font-medium text-foyr-deep">Action required:</div>
                      <div>1. Deploy the agent to the cluster (Helm chart in <code>infra/helm/foyr-hgm/</code>).</div>
                      <div>2. Bind <code>AGENT_BASE_URL</code> in Cloudflare Pages → Settings → Environment so <code>/api/connections/{app.name}</code> resolves.</div>
                      <div>3. Reopen this modal to retry.</div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="text-[10.5px] text-ink-500 leading-snug">{copy.hint}</div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-4">
            {current.status === "connected" ? (
              <button onClick={unwire} className="btn btn-outline !text-xs !py-2">Disconnect</button>
            ) : (
              <>
                <button onClick={onClose} className="btn btn-outline !text-xs !py-2">Cancel</button>
                <button onClick={primary} disabled={primaryDisabled} className="btn btn-foyr !text-xs !py-2 inline-flex items-center gap-1.5">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : app.auth === "oauth2" ? <ExternalLink className="w-3.5 h-3.5" /> : null}
                  {busy ? "Working…" : backendUnbound ? "Backend not bound" : copy.primaryLabel(app.name)}
                </button>
              </>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-border text-[10.5px] text-ink-500 leading-snug">
            All credentials are encrypted at rest. The cockpit only ever sees provider names + last-4 digits.
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  ), document.body);
}

function CopyField({ label, value, secret = false }: { label: string; value: string; secret?: boolean }): JSX.Element {
  const [show, setShow] = useState(!secret);
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-500 mb-1">{label}</div>
      <div className="flex items-center gap-1.5">
        <input
          readOnly
          type={show ? "text" : "password"}
          value={value}
          className="flex-1 bg-cream border border-border rounded-lg px-2.5 py-1.5 text-[12px] font-mono"
        />
        {secret ? (
          <button onClick={() => setShow((s) => !s)} className="btn btn-outline !text-[10px] !py-1.5">
            {show ? "Hide" : "Show"}
          </button>
        ) : null}
        <button
          onClick={() => { void navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
          className="btn btn-outline !text-[10px] !py-1.5 inline-flex items-center gap-1"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
